import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  API_BASE_URL,
  addFriend,
  changeUsername,
  createCheckout,
  createProfileComment,
  deleteProfileComment,
  getFriends,
  getFriendsByUsername,
  getMatchHistory,
  getMe,
  getPlayerRank,
  getProfile,
  getProfileComments,
  sendPartyInvite,
  updateProfile,
  uploadAvatar,
} from '../api/api';

const C = {
  red: '#E8192C',
  orange: '#FF6B2B',
  green: '#3FCB7E',
  bg: '#0C0905',
  card: '#111111',
  cardAlt: '#16110D',
  border: '#1e1e1e',
  borderBright: '#2A2520',
  text: '#F0F2F5',
  textDim: '#9A938B',
  textMuted: '#5C554E',
};

const F = {
  display: '"Bebas Neue", "Oswald", Impact, sans-serif',
  body: '"Rajdhani", "Inter", system-ui, sans-serif',
};

const BANNERS = [
  { id: 'red', label: 'Crimson', value: 'linear-gradient(135deg, #1a0305 0%, #3a0810 52%, #110304 100%)' },
  { id: 'orange', label: 'Ember', value: 'linear-gradient(135deg, #1a0a03 0%, #3a1a06 52%, #120703 100%)' },
  { id: 'dark', label: 'Void', value: 'linear-gradient(135deg, #080808 0%, #1b1b1b 52%, #050505 100%)' },
  { id: 'blue', label: 'Ocean', value: 'linear-gradient(135deg, #030a1a 0%, #061830 52%, #030712 100%)' },
  { id: 'purple', label: 'Shadow', value: 'linear-gradient(135deg, #0a0315 0%, #180630 52%, #07030f 100%)' },
  { id: 'gold', label: 'Legacy', value: 'linear-gradient(135deg, #1a1403 0%, #2a2008 52%, #100c02 100%)' },
  { id: 'neon-grid', label: 'Neon Grid', value: 'radial-gradient(circle at 20% 20%, rgba(232,25,44,.38), transparent 28%), linear-gradient(135deg, rgba(232,25,44,.18) 0 1px, transparent 1px 16px), linear-gradient(135deg, #090305, #160810 48%, #050505)' },
  { id: 'repeaked-flare', label: 'Repeaked Flare', value: 'radial-gradient(circle at 70% 20%, rgba(255,107,43,.42), transparent 26%), radial-gradient(circle at 20% 80%, rgba(232,25,44,.35), transparent 30%), linear-gradient(135deg, #170306, #090909)' },
  { id: 'midnight-smoke', label: 'Midnight Smoke', value: 'radial-gradient(circle at 10% 30%, rgba(92,85,78,.28), transparent 24%), radial-gradient(circle at 80% 60%, rgba(240,242,245,.12), transparent 22%), linear-gradient(135deg, #050505, #151515 55%, #090909)' },
  { id: 'aurora', label: 'Aurora', value: 'radial-gradient(circle at 25% 35%, rgba(63,203,126,.28), transparent 28%), radial-gradient(circle at 78% 45%, rgba(88,101,242,.28), transparent 30%), linear-gradient(135deg, #030b09, #080816 55%, #030507)' },
  { id: 'inferno', label: 'Inferno', value: 'radial-gradient(circle at 20% 70%, rgba(255,107,43,.48), transparent 24%), radial-gradient(circle at 80% 20%, rgba(232,25,44,.4), transparent 24%), linear-gradient(135deg, #1a0501, #220b03 50%, #060202)' },
];

const SOCIALS = [
  { key: 'twitter', label: 'X / Twitter', color: '#1DA1F2', placeholder: '@handle' },
  { key: 'twitch', label: 'Twitch', color: '#9146FF', placeholder: 'channel' },
  { key: 'youtube', label: 'YouTube', color: '#FF4E4E', placeholder: '@channel' },
  { key: 'discord', label: 'Discord', color: '#5865F2', placeholder: 'username' },
];

const emptyForm = {
  bio: '',
  country: '',
  banner: 'red',
  profile_visibility: 'public',
  friends_public: true,
  profile_comments_visibility: 'public',
  avatar_url: '',
  twitter: '',
  twitch: '',
  youtube: '',
  discord: '',
};

function formFromProfile(profile = {}) {
  return {
    bio: profile.bio || '',
    country: profile.country || '',
    banner: profile.banner || 'red',
    profile_visibility: profile.profile_visibility || 'public',
    friends_public: profile.friends_public !== false,
    profile_comments_visibility: profile.profile_comments_visibility || 'public',
    avatar_url: profile.avatar_url || '',
    twitter: profile.twitter || '',
    twitch: profile.twitch || '',
    youtube: profile.youtube || '',
    discord: profile.discord || '',
  };
}

function cleanPayload(form) {
  return {
    bio: form.bio.trim() || null,
    country: form.country.trim() || null,
    banner: form.banner,
    profile_visibility: form.profile_visibility,
    friends_public: Boolean(form.friends_public),
    profile_comments_visibility: form.profile_comments_visibility,
    avatar_url: form.avatar_url.trim() || null,
    twitter: form.twitter.trim() || null,
    twitch: form.twitch.trim() || null,
    youtube: form.youtube.trim() || null,
    discord: form.discord.trim() || null,
  };
}

const fmtNum = (value) => Number(value || 0).toLocaleString();
const initials = (name = '??') => name.slice(0, 2).toUpperCase();
const winRate = (stats = {}) => Math.round(((stats.wins || 0) / Math.max((stats.wins || 0) + (stats.losses || 0), 1)) * 100);
const kdRatio = (stats = {}) => (stats.deaths > 0 ? (stats.kills / stats.deaths).toFixed(2) : Number(stats.kills || 0).toFixed(2));
const rankSlug = (rankName) => String(rankName || 'unranked').toLowerCase().replace(/ /g, '-');
const rankAssetCandidates = (rankName) => {
  const slug = rankSlug(rankName);
  if (slug === 'unranked') {
    return [
      '/ranks/unranked.png',
      '/ranks/Unranked.png',
      '/ranks/unranked.webp',
      '/ranks/unranked.svg',
      '/assets/ranks/unranked.png',
    ];
  }
  return [`/ranks/${slug}.png`, `/ranks/${slug}.webp`, `/assets/ranks/${slug}.png`];
};
const bannerValue = (id) => BANNERS.find((banner) => banner.id === id)?.value || BANNERS[0].value;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : null;
const normalizeMapName = (map) => String(map || 'unknown').replace(/^de_/, '').replace(/_/g, ' ').toUpperCase();
const COMMENTS_PER_PAGE = 5;
const MODE_DEFAULTS = {
  fivevfive: { mode: 'fivevfive', mode_label: '5v5', placement_games_required: 5 },
  wingman: { mode: 'wingman', mode_label: 'Wingman', placement_games_required: 3 },
};

const RANK_MODE_TABS = [
  { id: 'fivevfive', label: '5v5' },
  { id: 'wingman', label: 'Wingman' },
];

function normalizeRankMode(mode) {
  const value = String(mode || 'fivevfive').toLowerCase();
  if (value === 'wingman' || value === '2v2') return 'wingman';
  return 'fivevfive';
}

function rankList(rankPayload) {
  const rawRanks = Array.isArray(rankPayload?.ranks) ? rankPayload.ranks : (rankPayload ? [rankPayload] : []);
  const ranks = rawRanks
    .filter(Boolean)
    .map((rank) => {
      const mode = normalizeRankMode(rank.mode);
      const defaults = MODE_DEFAULTS[mode] || MODE_DEFAULTS.fivevfive;
      return {
        ...defaults,
        ...rank,
        is_ranked: Boolean(rank.is_ranked),
        rank_name: rank.is_ranked ? (rank.rank_name || 'Unranked') : 'Unranked',
        elo: rank.is_ranked ? rank.elo : null,
        placement_games_played: Number(rank.placement_games_played ?? rank.placement_matches_played ?? 0),
        placement_games_required: Number(rank.placement_games_required ?? rank.placement_matches_required ?? defaults.placement_games_required),
      };
    });

  return Object.values(MODE_DEFAULTS).map((defaults) => {
    const existing = ranks.find((rank) => normalizeRankMode(rank.mode) === defaults.mode);
    return existing || {
      ...defaults,
      is_ranked: false,
      rank_name: 'Unranked',
      elo: null,
      placement_games_played: 0,
      placement_games_required: defaults.placement_games_required,
    };
  });
}

function primaryRank(rankPayload) {
  const ranks = rankList(rankPayload);
  return ranks.find((item) => normalizeRankMode(item.mode) === 'fivevfive') || rankPayload?.primary || ranks[0] || null;
}

function rankForMode(rankPayload, mode) {
  const ranks = rankList(rankPayload);
  const normalizedMode = normalizeRankMode(mode);
  return ranks.find((item) => normalizeRankMode(item.mode) === normalizedMode) || primaryRank(rankPayload);
}

function rankStatus(rank) {
  if (!rank) return 'Unranked';
  if (rank.is_ranked) return `${fmtNum(rank.elo)} ELO`;
  const required = rank.placement_games_required || (normalizeRankMode(rank.mode) === 'wingman' ? 3 : 5);
  return `${rank.placement_games_played || 0}/${required} placements`;
}

function RankBadge({ rank, size = 64 }) {
  const label = rank?.rank_name || 'Unranked';
  const sources = rankAssetCandidates(label);
  const [sourceIndex, setSourceIndex] = useState(0);
  const src = sources[sourceIndex];

  useEffect(() => {
    setSourceIndex(0);
  }, [label]);

  if (!src) {
    return <span className="rp-rank-fallback" style={{ width: size, height: size }}>?</span>;
  }

  return (
    <img
      src={src}
      alt=""
      aria-hidden="true"
      style={{ width: size, height: size }}
      onError={() => setSourceIndex((current) => current + 1)}
    />
  );
}

function fmtWait(seconds) {
  const safeSeconds = Math.max(1, Number(seconds) || 1);
  if (safeSeconds >= 60) {
    const minutes = Math.ceil(safeSeconds / 60);
    return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
  }
  return `${safeSeconds} ${safeSeconds === 1 ? 'second' : 'seconds'}`;
}

function commentRateLimitMessage(error) {
  if (error?.response?.status !== 429) return null;

  const data = error.response?.data || {};
  const detail = data.detail || {};
  const retryAfter = detail.retry_after_seconds
    || data.retry_after_seconds
    || error.response?.headers?.['retry-after'];
  if (detail.message && typeof detail.message === 'string') return detail.message;
  if (data.message && typeof data.message === 'string') return data.message;
  return `You're commenting a bit too quickly. Please wait ${fmtWait(retryAfter)} before posting again.`;
}

function apiErrorMessage(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail?.message && typeof detail.message === 'string') return detail.message;
  return fallback;
}

function socialHref(key, value) {
  const handle = String(value || '').trim().replace(/^@/, '');
  if (!handle || key === 'discord') return null;
  if (key === 'twitter') return `https://x.com/${encodeURIComponent(handle)}`;
  if (key === 'twitch') return `https://twitch.tv/${encodeURIComponent(handle)}`;
  if (key === 'youtube') return `https://youtube.com/${encodeURIComponent(handle)}`;
  return null;
}

function Eyebrow({ children }) {
  return <div className="rp-eyebrow"><span className="rp-diamond" />{children}</div>;
}

function Card({ eyebrow, title, action, children, className = '' }) {
  return (
    <section className={`rp-card ${className}`}>
      <div className="rp-card-head">
        <div>{eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}{title && <h2>{title}</h2>}</div>
        {action}
      </div>
      {children}
    </section>
  );
}

function ProfileModal({ labelledBy, children }) {
  return createPortal(
    <div className="rp-modal" role="dialog" aria-modal="true" aria-labelledby={labelledBy}>
      {children}
    </div>,
    document.body
  );
}

function Avatar({ profile, username, size = 104 }) {
  return (
    <div className="rp-avatar" style={{ width: size, height: size }}>
      {profile?.avatar_url ? <img src={profile.avatar_url} alt={`${username} avatar`} /> : <span>{initials(username)}</span>}
    </div>
  );
}

function SteamLogo({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M11.979 0C5.678 0 .511 4.86.022 11.037l6.432 2.658c.545-.371 1.203-.59 1.912-.59.063 0 .125.004.188.006l2.861-4.142V8.91c0-2.495 2.028-4.524 4.524-4.524 2.494 0 4.524 2.031 4.524 4.527s-2.03 4.525-4.524 4.525h-.105l-4.076 2.911c0 .052.004.105.004.159 0 1.875-1.515 3.396-3.39 3.396-1.635 0-3.016-1.173-3.331-2.727L.436 15.27C1.862 20.307 6.486 24 11.979 24c6.624 0 11.998-5.373 11.998-12.001C23.977 5.373 18.603 0 11.979 0z" />
    </svg>
  );
}

function SocialLinks({ profile }) {
  const visible = SOCIALS.filter((item) => profile?.[item.key]);
  if (!visible.length) return null;

  return (
    <div className="rp-social-row">
      {visible.map((item) => {
        const href = socialHref(item.key, profile[item.key]);
        const label = `${item.label}: ${profile[item.key]}`;
        return href
          ? <a key={item.key} href={href} target="_blank" rel="noreferrer" style={{ color: item.color }}>{label}</a>
          : <span key={item.key} style={{ color: item.color }}>{label}</span>;
      })}
    </div>
  );
}

function ProfileOptionsMenu({ username, onAddFriend, onInvite }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="rp-profile-menu-wrap">
      <button className="rp-profile-dots" aria-label={`Profile options for ${username}`} onClick={() => setOpen((value) => !value)}>
        ...
      </button>
      {open && (
        <div className="rp-profile-menu">
          <button onClick={() => { setOpen(false); onAddFriend(username); }}>Add Friend</button>
          <button onClick={() => { setOpen(false); onInvite(username); }}>Invite to Party</button>
        </div>
      )}
    </div>
  );
}

function steamTrustLabel(profile) {
  if (!profile?.steam_id) return 'Link Steam';

  switch (profile.steam_trust_status) {
    case 'trusted':
      return 'Steam Trusted';
    case 'blocked':
      return 'Steam Blocked';
    case 'suspicious':
      return 'Steam Pending Review';
    case 'unchecked':
      return 'Steam Linked - Not Checked';
    default:
      return 'Steam Linked - Not Checked';
  }
}

function ProfileHeader({ profile, stats, rank, username, isOwnProfile, onEdit, onAddFriend, onInviteProfile, actionStatus }) {
  const privateProfile = profile?.profile_visibility === 'private';
  const headerRank = primaryRank(rank);
  const linkSteam = () => {
    window.location.href = `${API_BASE_URL}/auth/steam/link`;
  };

  return (
    <header className="rp-hero" style={{ background: bannerValue(profile?.banner) }}>
      <div className="rp-noise" />
      <div className="rp-hero-actions">
        {isOwnProfile && (
          profile?.steam_id ? (
            <button className="rp-steam-btn is-linked" disabled>
              <span className="rp-steam-mark"><SteamLogo /></span> {steamTrustLabel(profile)}
            </button>
          ) : (
            <button className="rp-steam-btn" onClick={linkSteam}>
              <span className="rp-steam-mark"><SteamLogo /></span> Link Steam
            </button>
          )
        )}
        {isOwnProfile && <button className="rp-outline-btn" onClick={onEdit}>Customise Profile</button>}
        {!isOwnProfile && (
          <ProfileOptionsMenu
            username={profile?.username || username}
            onAddFriend={onAddFriend}
            onInvite={onInviteProfile}
          />
        )}
        {!isOwnProfile && actionStatus && <div className="rp-hero-action-status">{actionStatus}</div>}
      </div>

      <div className="rp-hero-main">
        <Avatar profile={profile} username={username} size={96} />
        <div className="rp-hero-copy">
          <Eyebrow>{isOwnProfile ? 'Your Player Card' : 'Player Profile'}</Eyebrow>
          <h1>{profile?.username || username}</h1>
          <div className="rp-username">Username</div>
          <div className="rp-rank-line">
            <RankBadge rank={headerRank} size={28} />
            <span>{headerRank?.rank_name || 'Unranked'}</span>
            <b>{rankStatus(headerRank)}</b>
            <b>LVL {stats?.level || 1}</b>
            {privateProfile && <b>Private</b>}
          </div>
          {profile?.bio && <p>{profile.bio}</p>}
          {profile?.country && <div className="rp-country">{profile.country}</div>}
          <SocialLinks profile={profile} />
        </div>
      </div>
    </header>
  );
}

function StatStrip({ stats, rank }) {
  const headerRank = primaryRank(rank);
  const items = [
    { label: '5v5 Rank', value: headerRank?.is_ranked ? headerRank.rank_name : 'Unranked' },
    { label: 'Win Rate', value: `${winRate(stats)}%` },
    { label: 'K/D', value: kdRatio(stats) },
    { label: 'Matches', value: fmtNum(stats?.matches_played) },
    { label: 'Streak', value: stats?.win_streak > 0 ? `${stats.win_streak}W` : `${stats?.loss_streak || 0}L` },
  ];

  return (
    <div className="rp-stat-strip">
      {items.map((item) => <div key={item.label}><strong>{item.value}</strong><span>{item.label}</span></div>)}
    </div>
  );
}

function RankModeToggle({ selectedMode, onSelectMode }) {
  return (
    <div className="rp-rank-toggle" aria-label="Rank mode">
      {RANK_MODE_TABS.map((tab) => (
        <button
          key={tab.id}
          type="button"
          className={selectedMode === tab.id ? 'is-active' : ''}
          onClick={() => onSelectMode(tab.id)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

function RankProgress({ rank, selectedMode, onSelectMode }) {
  const modeRank = rankForMode(rank, selectedMode);
  return (
    <Card
      eyebrow="Rank"
      title="Placement Progress"
      action={<RankModeToggle selectedMode={selectedMode} onSelectMode={onSelectMode} />}
    >
      {modeRank ? (
        <div className="rp-rank-card">
          <div className="rp-rank-top">
            <RankBadge rank={modeRank} />
            <div>
              <strong>{modeRank.rank_name || 'Unranked'}</strong>
              <span>{modeRank.is_ranked ? `${fmtNum(modeRank.elo)} ELO · ${modeRank.mode_label}` : `${modeRank.mode_label} · Unranked`}</span>
            </div>
          </div>
          <div className="rp-progress"><span style={{ width: `${Math.min(modeRank.progress_percent || 0, 100)}%` }} /></div>
          <div className="rp-rank-note">
            {modeRank.is_ranked
              ? (modeRank.elo_to_next_rank > 0 ? `${modeRank.elo_to_next_rank} ELO to next rank` : 'Max rank reached')
              : (() => {
                const required = modeRank.placement_games_required || (normalizeRankMode(modeRank.mode) === 'wingman' ? 3 : 5);
                return `Play ${required} ranked ${modeRank.mode_label || 'mode'} matches to reveal your rank. ${modeRank.placement_games_played || 0}/${required} complete.`;
              })()}
          </div>
        </div>
      ) : <div className="rp-empty">No rank data yet.</div>}
    </Card>
  );
}

function CareerStats({ stats }) {
  const items = [
    { label: 'Kills', value: fmtNum(stats?.kills) },
    { label: 'Deaths', value: fmtNum(stats?.deaths) },
    { label: 'Assists', value: fmtNum(stats?.assists) },
    { label: 'Wins', value: fmtNum(stats?.wins) },
    { label: 'Losses', value: fmtNum(stats?.losses) },
    { label: 'HS %', value: `${stats?.hs_percent || 0}%` },
  ];

  return (
    <Card eyebrow="Career" title="Stats">
      <div className="rp-metric-grid">
        {items.map((item) => <div key={item.label} className="rp-metric"><strong>{item.value}</strong><span>{item.label}</span></div>)}
      </div>
    </Card>
  );
}

function RecentFormStrip({ matches }) {
  const recent = matches.slice(0, 16).reverse();

  return (
    <Card eyebrow="Performance" title="Recent Form">
      {!recent.length ? <div className="rp-empty">No recent performance data yet.</div> : (
        <div className="rp-form-strip-wrap">
          <div className="rp-form-strip" aria-label="Recent match form">
            {recent.map((match, index) => {
              const elo = Number(match.elo_change || 0);
              const won = match.result === 'win';
              const label = `${won ? 'Win' : 'Loss'} on ${normalizeMapName(match.map)} (${elo > 0 ? '+' : ''}${elo} ELO)`;
              return (
                <div
                  key={`${match.match_id || match.id || index}-segment`}
                  className={`rp-form-segment ${won ? 'is-win' : 'is-loss'}`}
                  title={label}
                  aria-label={label}
                >
                  <b>{won ? 'W' : 'L'}</b>
                  <small>{elo > 0 ? '+' : ''}{elo}</small>
                </div>
              );
            })}
          </div>
          <div className="rp-chart-note">Last {recent.length} matches</div>
        </div>
      )}
    </Card>
  );
}

function BestMapCard({ matches }) {
  const bestMap = useMemo(() => {
    const maps = new Map();
    matches.forEach((match) => {
      if (!match.map) return;
      const name = normalizeMapName(match.map);
      const current = maps.get(name) || { name, wins: 0, total: 0 };
      current.total += 1;
      if (match.result === 'win') current.wins += 1;
      maps.set(name, current);
    });

    return Array.from(maps.values())
      .map((item) => ({ ...item, winrate: Math.round((item.wins / Math.max(item.total, 1)) * 100) }))
      .sort((a, b) => b.winrate - a.winrate || b.total - a.total || a.name.localeCompare(b.name))[0];
  }, [matches]);

  return (
    <Card eyebrow="Map Edge" title="Best Map">
      {!bestMap ? <div className="rp-empty">Not enough map data yet.</div> : (
        <div className="rp-best-map">
          <div>
            <strong>{bestMap.name}</strong>
            <span>{bestMap.wins}W / {bestMap.total - bestMap.wins}L</span>
          </div>
          <div className="rp-map-rate">
            <b>{bestMap.winrate}%</b>
            <small>Win rate</small>
          </div>
        </div>
      )}
    </Card>
  );
}

function MatchRow({ match, index, placeholder = false }) {
  if (placeholder) {
    return (
      <div className="rp-match rp-placeholder-row">
        <strong>-</strong>
        <span>Awaiting match</span>
        <b>-</b>
        <em>0</em>
        <small>TBD</small>
      </div>
    );
  }

  const won = match.result === 'win';
  const elo = Number(match.elo_change || 0);
  const date = match.finished_at ? new Date(match.finished_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : 'TBD';
  return (
    <div key={`${match.match_id || match.id || index}-${date}`} className={`rp-match ${won ? 'is-win' : ''}`}>
      <strong>{won ? 'WIN' : 'LOSS'}</strong>
      <span>{normalizeMapName(match.map)}</span>
      <b>{match.score || '-'}</b>
      <em>{elo > 0 ? '+' : ''}{elo}</em>
      <small>{date}</small>
    </div>
  );
}

function MatchHistory({ matches, modalOpen, setModalOpen }) {
  const visible = matches.slice(0, 3);
  const placeholders = Array.from({ length: Math.max(3 - visible.length, 0) });

  return (
    <>
      <Card
        eyebrow="Recent"
        title="Match History"
        action={matches.length > 3 ? <button className="rp-mini-btn" onClick={() => setModalOpen(true)}>View all {matches.length}</button> : null}
      >
        <div className="rp-match-list">
          {visible.map((match, index) => <MatchRow key={match.match_id || match.id || index} match={match} index={index} />)}
          {placeholders.map((_, index) => <MatchRow key={`match-placeholder-${index}`} placeholder />)}
        </div>
      </Card>
      {modalOpen && (
        <ProfileModal labelledBy="match-history-modal-title">
          <div className="rp-panel rp-list-panel">
            <div className="rp-panel-head">
              <div><Eyebrow>Recent</Eyebrow><h2 id="match-history-modal-title">All Matches</h2></div>
              <button className="rp-close" onClick={() => setModalOpen(false)} aria-label="Close match history">x</button>
            </div>
            <div className="rp-modal-list rp-match-list">
              {matches.map((match, index) => <MatchRow key={match.match_id || match.id || index} match={match} index={index} />)}
            </div>
          </div>
        </ProfileModal>
      )}
    </>
  );
}

function ProfileComments({
  comments,
  meta,
  loading,
  error,
  draft,
  setDraft,
  saving,
  deletingId,
  submitError,
  onSubmit,
  onDelete,
  isOwnProfile,
}) {
  const visibility = meta?.comments_visibility || 'public';
  const canComment = Boolean(meta?.can_comment);
  const [commentPage, setCommentPage] = useState(1);
  const previousCommentCount = useRef(comments.length);
  const pageCount = Math.max(1, Math.ceil(comments.length / COMMENTS_PER_PAGE));
  const pageStart = (commentPage - 1) * COMMENTS_PER_PAGE;
  const visibleComments = comments.slice(pageStart, pageStart + COMMENTS_PER_PAGE);

  useEffect(() => {
    setCommentPage((currentPage) => {
      const nextPageCount = Math.max(1, Math.ceil(comments.length / COMMENTS_PER_PAGE));
      return comments.length > previousCommentCount.current ? 1 : Math.min(currentPage, nextPageCount);
    });
    previousCommentCount.current = comments.length;
  }, [comments.length]);

  return (
    <Card eyebrow="Community" title="Comments" className="rp-wide">
      {loading ? <div className="rp-empty">Loading comments...</div> : (
        <div className="rp-comments">
          {error && <div className="rp-error" role="alert">{error}</div>}
          <div className="rp-comment-policy">
            {visibility === 'private'
              ? 'Comments are private: only friends can post.'
              : 'Comments are public: Steam-linked players can post.'}
          </div>
          {canComment ? (
            <div className="rp-comment-box">
              <textarea
                aria-label="Profile comment"
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                maxLength={500}
                placeholder="Leave a comment..."
              />
              <div className="rp-comment-actions">
                <small>{draft.length}/500</small>
                <button className="rp-primary-btn" onClick={onSubmit} disabled={saving || !draft.trim()}>
                  {saving ? 'Posting...' : 'Post Comment'}
                </button>
              </div>
              {submitError && <div className="rp-comment-submit-error" role="alert">{submitError}</div>}
            </div>
          ) : (
            <div className="rp-empty">
              {visibility === 'private'
                ? 'Only friends can comment on this profile.'
                : 'Link Steam to comment on this profile.'}
              {isOwnProfile && ' You can change comment visibility in Customise Profile.'}
            </div>
          )}
          {!comments.length ? <div className="rp-empty">No comments yet.</div> : (
            <>
              <div className="rp-comment-list">
                {visibleComments.map((comment) => (
                  <div key={comment.id} className="rp-comment">
                    {comment.author?.avatar_url ? <img src={comment.author.avatar_url} alt="" /> : <span>{initials(comment.author?.username || '??')}</span>}
                    <div>
                      <div className="rp-comment-head">
                        <b>{comment.author?.username || 'Unknown'}</b>
                        <small>{fmtDate(comment.created_at) || 'Just now'}</small>
                      </div>
                      <p>{comment.content}</p>
                      {comment.can_delete && (
                        <button className="rp-mini-btn" onClick={() => onDelete(comment.id)} disabled={deletingId === comment.id}>
                          {deletingId === comment.id ? 'Deleting...' : 'Delete'}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {comments.length > COMMENTS_PER_PAGE && (
                <div className="rp-comment-pagination" aria-label="Comment pagination">
                  <button className="rp-mini-btn" onClick={() => setCommentPage((page) => Math.max(1, page - 1))} disabled={commentPage === 1}>
                    Previous
                  </button>
                  <span>Page {commentPage} of {pageCount}</span>
                  <button className="rp-mini-btn" onClick={() => setCommentPage((page) => Math.min(pageCount, page + 1))} disabled={commentPage === pageCount}>
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </Card>
  );
}

function FriendRow({ friend, index, navigate, onInvite, placeholder = false }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const friendUser = friend?.users || {};
  const uname = friendUser.username || friend?.username || 'unknown';

  if (placeholder) {
    return (
      <div className="rp-friend-row rp-placeholder-row">
        <span>{initials('--')}</span>
        <b>Empty friend slot</b>
      </div>
    );
  }

  return (
    <div className="rp-friend-row">
      <button className="rp-friend-main" onClick={() => navigate(`/profile/${uname}`)}>
        {friendUser.avatar_url ? <img src={friendUser.avatar_url} alt="" /> : <span>{initials(uname)}</span>}
        <b>{uname}</b>
      </button>
      <div className="rp-friend-menu-wrap">
        <button
          className="rp-dots-btn"
          aria-label={`Friend options for ${uname}`}
          onClick={(event) => {
            event.stopPropagation();
            setMenuOpen((value) => !value);
          }}
        >
          ...
        </button>
        {menuOpen && (
          <div className="rp-friend-menu">
            <button onClick={() => navigate(`/profile/${uname}`)}>View Profile</button>
            <button onClick={() => { setMenuOpen(false); onInvite(uname); }}>Invite to Party</button>
          </div>
        )}
      </div>
    </div>
  );
}

function Friends({ friends, privateList, navigate, onInviteFriend, inviteStatus, modalOpen, setModalOpen }) {
  const visibleFriends = friends.slice(0, 3);
  const placeholders = Array.from({ length: Math.max(3 - visibleFriends.length, 0) });

  return (
    <>
      <Card
        eyebrow="Social"
        title="Friends"
        action={!privateList && friends.length > 3 ? <button className="rp-mini-btn" onClick={() => setModalOpen(true)}>View all {friends.length}</button> : null}
      >
        {privateList ? <div className="rp-empty">This player's friends list is private.</div> : (
          <div className="rp-friends">
            {visibleFriends.map((friend, index) => <FriendRow key={`${friend.users?.username || friend.username || 'friend'}-${index}`} friend={friend} index={index} navigate={navigate} onInvite={onInviteFriend} />)}
            {placeholders.map((_, index) => <FriendRow key={`friend-placeholder-${index}`} placeholder navigate={navigate} onInvite={onInviteFriend} />)}
          </div>
        )}
        {inviteStatus && <div className="rp-inline-status">{inviteStatus}</div>}
      </Card>
      {modalOpen && (
        <ProfileModal labelledBy="friends-modal-title">
          <div className="rp-panel rp-list-panel">
            <div className="rp-panel-head">
              <div><Eyebrow>Social</Eyebrow><h2 id="friends-modal-title">All Friends</h2></div>
              <button className="rp-close" onClick={() => setModalOpen(false)} aria-label="Close friends list">x</button>
            </div>
            <div className="rp-modal-list rp-friends">
              {friends.map((friend, index) => <FriendRow key={`${friend.users?.username || friend.username || 'friend'}-${index}`} friend={friend} index={index} navigate={navigate} onInvite={onInviteFriend} />)}
            </div>
          </div>
        </ProfileModal>
      )}
    </>
  );
}

function EditProfilePanel({
  form,
  setForm,
  profile,
  usernameDraft,
  setUsernameDraft,
  onChangeUsername,
  usernameSaving,
  usernameError,
  onAvatarUpload,
  avatarUploading,
  avatarError,
  onUpgrade,
  onClose,
  onSave,
  saving,
  error,
}) {
  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const selectedBanner = BANNERS.find((banner) => banner.id === form.banner) || BANNERS[0];
  const usernameBlocked = profile?.can_change_username === false && !profile?.has_premium;
  const usernameAvailableAt = fmtDate(profile?.username_change_available_at);

  return (
    <ProfileModal labelledBy="profile-edit-title">
      <div className="rp-panel">
        <div className="rp-panel-head">
          <div><Eyebrow>Customization</Eyebrow><h2 id="profile-edit-title">Edit Profile</h2></div>
          <button className="rp-close" onClick={onClose} aria-label="Close profile editor">x</button>
        </div>
        {error && <div className="rp-error" role="alert">{error}</div>}
        <div className="rp-username-editor">
          <div>
            <div className="rp-label">Username</div>
            <div className="rp-current-username">@{profile?.username}</div>
            <small>
              {profile?.has_premium
                ? 'Premium Pass active: username cooldown is bypassed.'
                : usernameBlocked
                  ? `Next free username change available ${usernameAvailableAt || 'soon'}.`
                  : 'Free username changes are available once every 30 days.'}
            </small>
          </div>
          <div className="rp-username-action">
            <input
              aria-label="New username"
              value={usernameDraft}
              onChange={(event) => setUsernameDraft(event.target.value)}
              maxLength={24}
              placeholder="new_username"
            />
            <button className="rp-outline-btn" onClick={onChangeUsername} disabled={usernameSaving || usernameBlocked}>
              {usernameSaving ? 'Changing...' : 'Change Username'}
            </button>
          </div>
          {usernameBlocked && <button className="rp-primary-btn" onClick={onUpgrade}>Get Premium Pass</button>}
          {usernameError && <div className="rp-error" role="alert">{usernameError}</div>}
        </div>
        <label>Bio<textarea value={form.bio} onChange={(event) => update('bio', event.target.value)} maxLength={180} placeholder="Tell players who you are." /><small>{form.bio.length}/180</small></label>
        <label>Country / region<input value={form.country} onChange={(event) => update('country', event.target.value)} maxLength={32} placeholder="United Kingdom" /></label>
        <div className="rp-avatar-editor">
          <div>
            <div className="rp-label">Profile photo</div>
            <div className="rp-avatar-preview">
              {form.avatar_url ? <img src={form.avatar_url} alt="Avatar preview" /> : <span>{initials(profile?.username)}</span>}
            </div>
          </div>
          <div className="rp-avatar-controls">
            <label>
              Avatar URL
              <input value={form.avatar_url} onChange={(event) => update('avatar_url', event.target.value)} placeholder="https://i.imgur.com/avatar.jpg" />
              <small>Paste an HTTPS image URL, or upload from your PC.</small>
            </label>
            <label className="rp-file-upload">
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={(event) => {
                  onAvatarUpload(event.target.files?.[0]);
                  event.target.value = '';
                }}
                disabled={avatarUploading}
              />
              <span>{avatarUploading ? 'Uploading photo...' : 'Upload photo from PC'}</span>
            </label>
            {avatarError && <div className="rp-error" role="alert">{avatarError}</div>}
          </div>
        </div>
        <div className="rp-banner-editor">
          <label>
            Banner preset
            <select aria-label="Banner preset" value={form.banner} onChange={(event) => update('banner', event.target.value)}>
              {BANNERS.map((banner) => <option key={banner.id} value={banner.id}>{banner.label}</option>)}
            </select>
          </label>
          <div className="rp-banner-preview" style={{ background: selectedBanner.value }}>
            <span>{selectedBanner.label}</span>
          </div>
        </div>
        <div className="rp-form-grid">
          <label>Profile visibility<select value={form.profile_visibility} onChange={(event) => update('profile_visibility', event.target.value)}><option value="public">Public</option><option value="private">Private</option></select></label>
          <label>Friends list<select value={form.friends_public ? 'public' : 'private'} onChange={(event) => update('friends_public', event.target.value === 'public')}><option value="public">Public</option><option value="private">Private</option></select></label>
          <label>Comments<select value={form.profile_comments_visibility} onChange={(event) => update('profile_comments_visibility', event.target.value)}><option value="public">Public</option><option value="private">Friends only</option></select></label>
        </div>
        <div>
          <div className="rp-label">Social handles</div>
          <div className="rp-form-grid">
            {SOCIALS.map((social) => <label key={social.key}>{social.label}<input value={form[social.key]} onChange={(event) => update(social.key, event.target.value)} maxLength={40} placeholder={social.placeholder} /></label>)}
          </div>
        </div>
        <div className="rp-panel-actions">
          <button className="rp-outline-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="rp-primary-btn" onClick={onSave} disabled={saving}>{saving ? 'Saving...' : 'Save Profile'}</button>
        </div>
      </div>
    </ProfileModal>
  );
}

export default function Profile() {
  const { username } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const pageRef = useRef(null);

  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState({});
  const [rank, setRank] = useState(null);
  const [selectedRankMode, setSelectedRankMode] = useState('fivevfive');
  const [friends, setFriends] = useState([]);
  const [friendsPrivate, setFriendsPrivate] = useState(false);
  const [matches, setMatches] = useState([]);
  const [comments, setComments] = useState([]);
  const [commentsMeta, setCommentsMeta] = useState({ comments_visibility: 'public', can_comment: false });
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsError, setCommentsError] = useState('');
  const [commentSubmitError, setCommentSubmitError] = useState('');
  const [commentDraft, setCommentDraft] = useState('');
  const [commentSaving, setCommentSaving] = useState(false);
  const [commentDeletingId, setCommentDeletingId] = useState(null);
  const [friendInviteStatus, setFriendInviteStatus] = useState('');
  const [profileActionStatus, setProfileActionStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState('');
  const [editing, setEditing] = useState(false);
  const [friendsModalOpen, setFriendsModalOpen] = useState(false);
  const [matchesModalOpen, setMatchesModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [usernameDraft, setUsernameDraft] = useState('');
  const [usernameSaving, setUsernameSaving] = useState(false);
  const [usernameError, setUsernameError] = useState('');
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarError, setAvatarError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saved, setSaved] = useState(false);
  const cachedUsername = (() => {
    try {
      return JSON.parse(localStorage.getItem('cached_user') || '{}')?.username;
    } catch {
      return null;
    }
  })();
  const isOwnRoute = user?.username === username || cachedUsername === username;
  const isOwnProfile = Boolean(profile?.username === username && (isOwnRoute || (profile?.id && user?.id === profile.id)));
  const profileModalOpen = editing || friendsModalOpen || matchesModalOpen;

  useEffect(() => {
    let cancelled = false;

    async function loadProfile() {
      setLoading(true);
      setPageError('');
      setSaved(false);
      setProfile(null);
      setStats({});
      setRank(null);
      setFriends([]);
      setFriendsPrivate(false);
      setMatches([]);
      setComments([]);
      setCommentsMeta({ comments_visibility: 'public', can_comment: false });
      setCommentsError('');
      setCommentSubmitError('');
      setCommentDraft('');
      setFriendInviteStatus('');
      setProfileActionStatus('');
      setEditing(false);
      setFriendsModalOpen(false);
      setMatchesModalOpen(false);
      setCommentsLoading(true);

      try {
        const profileRes = await (isOwnRoute ? getMe() : getProfile(username));
        if (cancelled) return;

        const nextProfile = profileRes.data.user || {};
        setProfile(nextProfile);
        setStats(profileRes.data.stats || {});
        if (profileRes.data.ranks) {
          setRank({ ranks: profileRes.data.ranks });
        }
        setForm(formFromProfile(nextProfile));
        setUsernameDraft(nextProfile.username || '');
        setFriendsPrivate(nextProfile.friends_public === false || nextProfile.profile_visibility === 'private');

        const [rankRes, friendsRes, matchRes, commentsRes] = await Promise.allSettled([
          getPlayerRank(username),
          isOwnRoute ? getFriends() : getFriendsByUsername(username),
          getMatchHistory(username),
          getProfileComments(username),
        ]);

        if (cancelled) return;
        if (rankRes.status === 'fulfilled') setRank(rankRes.value.data);
        if (friendsRes.status === 'fulfilled') {
          setFriends(friendsRes.value.data.friends || []);
          setFriendsPrivate(Boolean(friendsRes.value.data.private));
        }
        if (matchRes.status === 'fulfilled') setMatches(matchRes.value.data.match_history || []);
        if (commentsRes.status === 'fulfilled') {
          setComments(commentsRes.value.data.comments || []);
          setCommentsMeta(commentsRes.value.data.meta || { comments_visibility: 'public', can_comment: false });
        } else {
          setCommentsError('Could not load profile comments.');
        }
      } catch (error) {
        if (!cancelled) setPageError(error.response?.data?.detail || 'Unable to load this profile.');
      } finally {
        if (!cancelled) {
          setCommentsLoading(false);
          setLoading(false);
        }
      }
    }

    loadProfile();
    return () => { cancelled = true; };
  }, [username, isOwnRoute]);

  useEffect(() => {
    if (!profileModalOpen) return undefined;

    const page = pageRef.current;
    const previousBodyOverflow = document.body.style.overflow;
    const previousPageOverflowY = page?.style.overflowY || '';

    document.body.style.overflow = 'hidden';
    if (page) page.style.overflowY = 'hidden';

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      if (page) page.style.overflowY = previousPageOverflowY;
    };
  }, [profileModalOpen]);

  const openEditor = () => {
    setForm(formFromProfile(profile));
    setUsernameDraft(profile?.username || '');
    setUsernameError('');
    setAvatarError('');
    setSaveError('');
    setEditing(true);
  };

  const saveCachedUsername = (nextUsername) => {
    try {
      const cached = JSON.parse(localStorage.getItem('cached_user') || '{}');
      localStorage.setItem('cached_user', JSON.stringify({ ...cached, username: nextUsername }));
    } catch {
      // Cache repair is best-effort; the backend remains the source of truth.
    }
  };

  const handleChangeUsername = async () => {
    setUsernameSaving(true);
    setUsernameError('');
    try {
      const nextUsername = usernameDraft.trim();
      const res = await changeUsername(nextUsername);
      const nextProfile = res.data.user || { ...profile, username: nextUsername };
      setProfile(nextProfile);
      setUsernameDraft(nextProfile.username || nextUsername);
      saveCachedUsername(nextProfile.username || nextUsername);
      navigate(`/profile/${nextProfile.username || nextUsername}`, { replace: true });
    } catch (error) {
      const detail = error.response?.data?.detail;
      setUsernameError(typeof detail === 'object' ? detail.message : detail || 'Username change failed.');
    } finally {
      setUsernameSaving(false);
    }
  };

  const handleUpgrade = async () => {
    try {
      const res = await createCheckout('premium_monthly');
      const url = res.data?.checkout_url || res.data?.url;
      if (url) window.location.href = url;
    } catch {
      setUsernameError('Could not start Premium Pass checkout.');
    }
  };

  const handleAvatarUpload = async (file) => {
    if (!file) return;
    setAvatarUploading(true);
    setAvatarError('');
    try {
      const res = await uploadAvatar(file);
      const nextProfile = res.data.user || { ...profile, avatar_url: res.data.avatar_url };
      setProfile(nextProfile);
      setForm(formFromProfile(nextProfile));
    } catch (error) {
      setAvatarError(error.response?.data?.detail || 'Avatar upload failed.');
    } finally {
      setAvatarUploading(false);
    }
  };

  const saveProfile = async () => {
    setSaving(true);
    setSaveError('');
    setSaved(false);
    try {
      const res = await updateProfile(cleanPayload(form));
      const nextProfile = res.data.user || { ...profile, ...cleanPayload(form) };
      setProfile(nextProfile);
      setForm(formFromProfile(nextProfile));
      setFriendsPrivate(nextProfile.friends_public === false || nextProfile.profile_visibility === 'private');
      setEditing(false);
      setSaved(true);
    } catch (error) {
      const detail = error.response?.data?.detail;
      setSaveError(Array.isArray(detail) ? detail.map((item) => item.msg).join(', ') : detail || 'Profile update failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateComment = async () => {
    const content = commentDraft.trim();
    if (!content) return;
    setCommentSaving(true);
    setCommentsError('');
    setCommentSubmitError('');
    try {
      const res = await createProfileComment(username, content);
      setComments((current) => [res.data.comment, ...current]);
      setCommentDraft('');
      setCommentsMeta((current) => ({ ...current, can_comment: true }));
    } catch (error) {
      const rateLimitMessage = commentRateLimitMessage(error);
      if (rateLimitMessage) {
        setCommentSubmitError(rateLimitMessage);
      } else {
        setCommentsError(apiErrorMessage(error, 'Could not post comment.'));
      }
    } finally {
      setCommentSaving(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    setCommentDeletingId(commentId);
    setCommentsError('');
    setCommentSubmitError('');
    try {
      await deleteProfileComment(username, commentId);
      setComments((current) => current.filter((comment) => comment.id !== commentId));
    } catch (error) {
      setCommentsError(error.response?.data?.detail || 'Could not delete comment.');
    } finally {
      setCommentDeletingId(null);
    }
  };

  const handleInviteFriend = async (friendUsername) => {
    setFriendInviteStatus('');
    try {
      await sendPartyInvite(friendUsername);
      setFriendInviteStatus(`Party invite sent to ${friendUsername}.`);
    } catch (error) {
      setFriendInviteStatus(error.response?.data?.detail || `Could not invite ${friendUsername}.`);
    }
  };

  const handleInviteProfile = async (targetUsername) => {
    setProfileActionStatus('');
    try {
      await sendPartyInvite(targetUsername);
      setProfileActionStatus(`Party invite sent to ${targetUsername}.`);
    } catch (error) {
      setProfileActionStatus(error.response?.data?.detail || `Could not invite ${targetUsername}.`);
    }
  };

  const handleAddProfileFriend = async (targetUsername) => {
    setProfileActionStatus('');
    try {
      await addFriend(targetUsername);
      setProfileActionStatus(`Friend request sent to ${targetUsername}.`);
    } catch (error) {
      setProfileActionStatus(error.response?.data?.detail || `Could not add ${targetUsername}.`);
    }
  };

  if (loading) {
    return <div className="rp-page rp-center"><ProfileStyles /><div className="rp-loading">Loading Profile...</div></div>;
  }

  if (pageError) {
    return <div className="rp-page rp-center"><ProfileStyles /><div className="rp-error">{pageError}</div></div>;
  }

  return (
    <div className="rp-page" ref={pageRef}>
      <ProfileStyles />
      {editing && (
        <EditProfilePanel
          form={form}
          setForm={setForm}
          profile={profile}
          usernameDraft={usernameDraft}
          setUsernameDraft={setUsernameDraft}
          onChangeUsername={handleChangeUsername}
          usernameSaving={usernameSaving}
          usernameError={usernameError}
          onAvatarUpload={handleAvatarUpload}
          avatarUploading={avatarUploading}
          avatarError={avatarError}
          onUpgrade={handleUpgrade}
          onClose={() => setEditing(false)}
          onSave={saveProfile}
          saving={saving}
          error={saveError}
        />
      )}
      <ProfileHeader
        profile={profile}
        stats={stats}
        rank={rank}
        username={username}
        isOwnProfile={isOwnProfile}
        onEdit={openEditor}
        onAddFriend={handleAddProfileFriend}
        onInviteProfile={handleInviteProfile}
        actionStatus={profileActionStatus}
      />
      {saved && <div className="rp-success" role="status">Profile saved.</div>}
      <StatStrip stats={stats} rank={rank} />
      <main className="rp-grid">
        <RankProgress rank={rank} selectedMode={selectedRankMode} onSelectMode={setSelectedRankMode} />
        <RecentFormStrip matches={matches} />
        <CareerStats stats={stats} />
        <BestMapCard matches={matches} />
        <Friends
          friends={friends}
          privateList={friendsPrivate && !isOwnProfile}
          navigate={navigate}
          onInviteFriend={handleInviteFriend}
          inviteStatus={friendInviteStatus}
          modalOpen={friendsModalOpen}
          setModalOpen={setFriendsModalOpen}
        />
        <MatchHistory matches={matches} modalOpen={matchesModalOpen} setModalOpen={setMatchesModalOpen} />
        <ProfileComments
          comments={comments}
          meta={commentsMeta}
          loading={commentsLoading}
          error={commentsError}
          draft={commentDraft}
          setDraft={setCommentDraft}
          saving={commentSaving}
          deletingId={commentDeletingId}
          submitError={commentSubmitError}
          onSubmit={handleCreateComment}
          onDelete={handleDeleteComment}
          isOwnProfile={isOwnProfile}
        />
      </main>
    </div>
  );
}

function ProfileStyles() {
  return (
    <style>{`
      @keyframes rpUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
      .rp-page { height: 100vh; overflow-y: auto; overflow-x: hidden; overscroll-behavior: contain; background: ${C.bg}; color: ${C.text}; font-family: ${F.body}; padding: 24px; animation: rpUp 360ms ease both; box-sizing: border-box; }
      .rp-center { display: flex; align-items: center; justify-content: center; }
      .rp-loading { font-family: ${F.display}; font-size: 26px; letter-spacing: 6px; color: ${C.textMuted}; }
      .rp-error, .rp-success { border: 1px solid rgba(232,25,44,.35); background: rgba(232,25,44,.08); color: ${C.text}; padding: 14px 16px; margin: 14px auto; max-width: 1400px; letter-spacing: 1px; }
      .rp-success { border-color: rgba(63,203,126,.35); background: rgba(63,203,126,.08); color: ${C.green}; }
      .rp-hero { max-width: 1400px; margin: 0 auto; min-height: 215px; border: 1px solid ${C.border}; position: relative; overflow: hidden; padding: 24px; display: flex; align-items: center; }
      .rp-noise { position: absolute; inset: 0; background-image: radial-gradient(circle at 20% 0%, rgba(232,25,44,.24), transparent 30%), linear-gradient(90deg, rgba(0,0,0,.35), rgba(0,0,0,.1)); pointer-events: none; }
      .rp-hero-actions { position: absolute; right: 22px; top: 22px; z-index: 2; display: flex; gap: 10px; flex-wrap: wrap; justify-content: flex-end; }
      .rp-hero-main { position: relative; z-index: 1; display: flex; gap: 16px; align-items: center; width: 100%; padding-top: 24px; }
      .rp-hero-copy h1 { font-family: ${F.display}; font-size: clamp(42px, 6vw, 72px); letter-spacing: 7px; margin: 4px 0 0; line-height: .9; text-shadow: 0 12px 32px rgba(0,0,0,.55); }
      .rp-hero-copy p { max-width: 720px; color: rgba(240,242,245,.72); line-height: 1.5; margin: 12px 0 0; font-size: 15px; }
      .rp-username, .rp-country { color: rgba(240,242,245,.45); letter-spacing: 2px; font-size: 12px; margin-top: 4px; }
      .rp-avatar { background: ${C.card}; border: 3px solid rgba(255,255,255,.12); display: flex; align-items: center; justify-content: center; overflow: hidden; flex-shrink: 0; box-shadow: 0 18px 50px rgba(0,0,0,.35); }
      .rp-avatar img { width: 100%; height: 100%; object-fit: cover; }
      .rp-avatar span { font-family: ${F.display}; font-size: 34px; letter-spacing: 3px; color: ${C.textMuted}; }
      .rp-rank-line, .rp-social-row { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; margin-top: 12px; }
      .rp-rank-line img { width: 28px; height: 28px; object-fit: contain; }
      .rp-rank-line span { color: ${C.orange}; font-weight: 800; letter-spacing: 2px; text-transform: uppercase; }
      .rp-rank-line b { color: rgba(240,242,245,.55); font-size: 12px; letter-spacing: 2px; text-transform: uppercase; }
      .rp-social-row a, .rp-social-row span { text-decoration: none; font-size: 11px; letter-spacing: 1.5px; font-weight: 800; text-transform: uppercase; }
      .rp-eyebrow { display: flex; align-items: center; gap: 10px; color: ${C.textDim}; font-size: 10px; font-weight: 800; letter-spacing: 2.5px; text-transform: uppercase; }
      .rp-diamond { width: 7px; height: 7px; display: inline-block; background: ${C.red}; transform: rotate(45deg); }
      .rp-stat-strip { max-width: 1400px; margin: 0 auto 18px; display: grid; grid-template-columns: repeat(5, 1fr); border: 1px solid ${C.border}; border-top: none; background: ${C.card}; }
      .rp-stat-strip div { padding: 18px; text-align: center; border-right: 1px solid ${C.border}; }
      .rp-stat-strip div:last-child { border-right: none; }
      .rp-stat-strip strong, .rp-metric strong { display: block; font-family: ${F.display}; font-size: 30px; letter-spacing: 3px; color: ${C.text}; line-height: 1; }
      .rp-stat-strip span, .rp-metric span { display: block; margin-top: 6px; color: ${C.textMuted}; font-size: 10px; letter-spacing: 2.5px; text-transform: uppercase; font-weight: 800; }
      .rp-grid { max-width: 1400px; margin: 0 auto; display: grid; grid-template-columns: minmax(280px, .82fr) minmax(420px, 1.38fr); gap: 18px; align-items: stretch; }
      .rp-wide { grid-column: 1 / -1; }
      .rp-column { display: flex; flex-direction: column; gap: 18px; }
      .rp-card { height: 100%; box-sizing: border-box; background: ${C.card}; border-top: 1px solid ${C.border}; border-right: 1px solid ${C.border}; border-bottom: 1px solid ${C.border}; border-left: 2px solid ${C.red}; padding: 22px; min-width: 0; }
      .rp-card-head { display: flex; justify-content: space-between; gap: 16px; align-items: flex-start; margin-bottom: 18px; }
      .rp-card h2, .rp-panel h2 { font-family: ${F.display}; font-size: 28px; letter-spacing: 4px; margin: 6px 0 0; line-height: 1; font-weight: 400; }
      .rp-rank-card, .rp-match-list { display: flex; flex-direction: column; gap: 12px; }
      .rp-rank-toggle { display: flex; gap: 6px; background: #080808; border: 1px solid ${C.border}; padding: 4px; }
      .rp-rank-toggle button { border: none; background: transparent; color: ${C.textMuted}; cursor: pointer; font-family: ${F.body}; font-size: 10px; font-weight: 900; letter-spacing: 1.5px; text-transform: uppercase; padding: 7px 9px; }
      .rp-rank-toggle button.is-active { background: ${C.red}; color: ${C.text}; }
      .rp-rank-top { display: flex; gap: 16px; align-items: center; }
      .rp-rank-top img { width: 64px; height: 64px; object-fit: contain; }
      .rp-rank-top strong { display: block; font-family: ${F.display}; font-size: 26px; letter-spacing: 3px; }
      .rp-rank-top span, .rp-rank-note { color: ${C.orange}; letter-spacing: 2px; font-size: 12px; text-transform: uppercase; }
      .rp-progress { height: 5px; background: #211b16; overflow: hidden; }
      .rp-progress span { display: block; height: 100%; background: linear-gradient(90deg, ${C.red}, ${C.orange}); }
      .rp-metric-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
      .rp-metric { background: ${C.cardAlt}; border: 1px solid ${C.border}; padding: 18px 12px; text-align: center; }
      .rp-form-strip-wrap { display: grid; gap: 10px; }
      .rp-form-strip { display: grid; grid-template-columns: repeat(16, minmax(0, 1fr)); min-height: 58px; background: #080808; border: 1px solid ${C.border}; overflow: hidden; }
      .rp-form-segment { min-width: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 3px; border-right: 1px solid rgba(255,255,255,.06); color: ${C.text}; }
      .rp-form-segment:last-child { border-right: none; }
      .rp-form-segment.is-win { background: linear-gradient(180deg, rgba(232,25,44,.34), rgba(232,25,44,.08)); }
      .rp-form-segment.is-loss { background: linear-gradient(180deg, rgba(92,85,78,.25), rgba(0,0,0,.18)); color: ${C.textMuted}; }
      .rp-form-segment b { font-family: ${F.display}; letter-spacing: 2px; line-height: 1; }
      .rp-form-segment small, .rp-chart-note { color: ${C.textMuted}; font-size: 9px; letter-spacing: 1px; text-transform: uppercase; }
      .rp-best-map { min-height: 118px; background: radial-gradient(circle at 20% 20%, rgba(232,25,44,.16), transparent 28%), ${C.cardAlt}; border: 1px solid ${C.border}; display: flex; align-items: center; justify-content: space-between; gap: 18px; padding: 18px; }
      .rp-best-map strong, .rp-map-rate b { display: block; font-family: ${F.display}; color: ${C.text}; line-height: .9; }
      .rp-best-map strong { font-size: clamp(32px, 5vw, 58px); letter-spacing: 5px; }
      .rp-best-map span, .rp-map-rate small { color: ${C.textMuted}; font-size: 11px; letter-spacing: 2px; text-transform: uppercase; font-weight: 800; }
      .rp-map-rate { text-align: right; border-left: 1px solid ${C.borderBright}; padding-left: 18px; }
      .rp-map-rate b { color: ${C.red}; font-size: 48px; letter-spacing: 4px; }
      .rp-match { display: grid; grid-template-columns: 58px 1fr 70px 64px 56px; gap: 12px; align-items: center; background: ${C.cardAlt}; border: 1px solid ${C.border}; border-left: 3px solid ${C.textMuted}; padding: 13px 14px; }
      .rp-match.is-win { border-left-color: ${C.red}; border-color: rgba(232,25,44,.25); }
      .rp-match strong { font-family: ${F.display}; letter-spacing: 3px; color: ${C.textMuted}; }
      .rp-match.is-win strong { color: ${C.red}; }
      .rp-match span, .rp-match b { letter-spacing: 2px; text-transform: uppercase; }
      .rp-match em { color: ${C.green}; font-style: normal; text-align: right; font-weight: 800; }
      .rp-match small { color: ${C.textMuted}; text-align: right; letter-spacing: 1px; }
      .rp-friends { display: grid; gap: 10px; }
      .rp-friend-row { min-height: 58px; display: grid; grid-template-columns: 1fr auto; align-items: center; gap: 8px; border: 1px solid ${C.border}; background: ${C.cardAlt}; padding: 8px; position: relative; box-sizing: border-box; }
      .rp-friend-main { display: flex; align-items: center; gap: 10px; border: none; background: transparent; color: ${C.text}; padding: 0; cursor: pointer; text-align: left; font-family: ${F.body}; min-width: 0; }
      .rp-friend-main img, .rp-friend-main span, .rp-friend-row > span { width: 34px; height: 34px; object-fit: cover; background: ${C.border}; display: flex; align-items: center; justify-content: center; color: ${C.textMuted}; font-family: ${F.display}; flex-shrink: 0; }
      .rp-friend-main b, .rp-friend-row b { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; letter-spacing: 1px; }
      .rp-dots-btn { width: 32px; height: 32px; border: 1px solid ${C.borderBright}; background: #080808; color: ${C.textDim}; cursor: pointer; letter-spacing: 1px; line-height: 1; }
      .rp-friend-menu-wrap { position: relative; }
      .rp-friend-menu { position: absolute; right: 0; top: calc(100% + 6px); z-index: 8; min-width: 150px; background: #080808; border: 1px solid ${C.borderBright}; box-shadow: 0 14px 34px rgba(0,0,0,.45); display: grid; padding: 6px; }
      .rp-friend-menu button { border: none; background: transparent; color: ${C.text}; text-align: left; padding: 9px 10px; cursor: pointer; font-family: ${F.body}; letter-spacing: 1px; }
      .rp-friend-menu button:hover { background: rgba(232,25,44,.14); }
      .rp-placeholder-row { opacity: .62; border-style: dashed; color: ${C.textMuted}; }
      .rp-inline-status { margin-top: 10px; color: ${C.textMuted}; border: 1px solid ${C.border}; background: rgba(255,255,255,.03); padding: 9px 10px; letter-spacing: 1px; }
      .rp-list-panel { width: min(760px, 100%); }
      .rp-modal-list { max-height: min(62vh, 620px); overflow: auto; padding-right: 4px; }
      .rp-comments { display: grid; gap: 12px; }
      .rp-comment-policy { background: rgba(255,255,255,.03); border: 1px solid ${C.border}; color: ${C.textMuted}; padding: 10px 12px; font-size: 11px; letter-spacing: 1.5px; text-transform: uppercase; }
      .rp-comment-box { background: ${C.cardAlt}; border: 1px solid ${C.border}; padding: 12px; display: grid; gap: 10px; }
      .rp-comment-box textarea { min-height: 78px; }
      .rp-comment-actions { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
      .rp-comment-actions small { color: ${C.textMuted}; letter-spacing: 1px; }
      .rp-comment-submit-error { border: 1px solid rgba(255,107,43,.32); background: rgba(255,107,43,.08); color: ${C.orange}; padding: 10px 12px; letter-spacing: 1px; line-height: 1.35; }
      .rp-comment-list { display: grid; gap: 10px; }
      .rp-comment-pagination { display: flex; align-items: center; justify-content: flex-end; gap: 12px; flex-wrap: wrap; }
      .rp-comment-pagination span { color: ${C.textMuted}; font-size: 11px; letter-spacing: 1.5px; text-transform: uppercase; font-weight: 800; }
      .rp-comment { display: grid; grid-template-columns: 42px 1fr; gap: 12px; background: ${C.cardAlt}; border: 1px solid ${C.border}; padding: 12px; }
      .rp-comment img, .rp-comment > span { width: 42px; height: 42px; object-fit: cover; background: #080808; border: 1px solid ${C.borderBright}; display: flex; align-items: center; justify-content: center; color: ${C.textMuted}; font-family: ${F.display}; }
      .rp-comment-head { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; }
      .rp-comment-head b { color: ${C.text}; letter-spacing: 1px; }
      .rp-comment-head small { color: ${C.textMuted}; letter-spacing: 1px; }
      .rp-comment p { margin: 7px 0 8px; color: rgba(240,242,245,.78); line-height: 1.45; white-space: pre-wrap; overflow-wrap: anywhere; }
      .rp-empty { color: ${C.textMuted}; border: 1px dashed ${C.borderBright}; padding: 24px; text-align: center; letter-spacing: 1px; }
      .rp-outline-btn, .rp-primary-btn, .rp-steam-btn, .rp-mini-btn { border: 1px solid rgba(255,255,255,.15); background: rgba(0,0,0,.28); color: ${C.text}; cursor: pointer; font-family: ${F.display}; letter-spacing: 2px; text-transform: uppercase; padding: 10px 14px; }
      .rp-primary-btn { background: ${C.red}; border-color: ${C.red}; }
      .rp-steam-btn { color: #66C0F4; border-color: rgba(102,192,244,.4); background: linear-gradient(135deg, rgba(27,40,56,.5), rgba(0,0,0,.28)); }
      .rp-steam-btn.is-linked { color: #66C0F4; border-color: rgba(102,192,244,.48); }
      .rp-steam-mark { display: inline-flex; align-items: center; justify-content: center; width: 18px; height: 18px; border-radius: 50%; margin-right: 8px; background: linear-gradient(135deg, #66C0F4 0%, #2A475E 100%); color: #0B1622; letter-spacing: 0; box-shadow: 0 0 12px rgba(102,192,244,.22); }
      .rp-steam-mark svg { width: 13px; height: 13px; display: block; }
      .rp-profile-menu-wrap { position: relative; }
      .rp-profile-dots { width: 42px; height: 38px; border: 1px solid rgba(255,255,255,.15); background: rgba(0,0,0,.28); color: ${C.text}; cursor: pointer; font-family: ${F.display}; letter-spacing: 2px; }
      .rp-profile-menu { position: absolute; right: 0; top: calc(100% + 8px); z-index: 9; min-width: 170px; background: #080808; border: 1px solid ${C.borderBright}; box-shadow: 0 14px 34px rgba(0,0,0,.45); display: grid; padding: 6px; }
      .rp-profile-menu button { border: none; background: transparent; color: ${C.text}; text-align: left; padding: 10px; cursor: pointer; font-family: ${F.body}; letter-spacing: 1px; }
      .rp-profile-menu button:hover { background: rgba(232,25,44,.14); }
      .rp-hero-action-status { width: 100%; color: ${C.textDim}; text-align: right; font-size: 11px; letter-spacing: 1px; }
      .rp-mini-btn { padding: 7px 10px; color: ${C.textDim}; background: transparent; }
      button:disabled { opacity: .55; cursor: not-allowed; }
      .rp-modal { position: fixed; inset: 0; background: rgba(0,0,0,.86); z-index: 300; display: flex; align-items: flex-start; justify-content: center; padding: 32px 20px; overflow-y: auto; box-sizing: border-box; }
      .rp-panel { width: min(820px, 100%); max-height: calc(100vh - 64px); overflow: auto; background: ${C.card}; border: 1px solid ${C.borderBright}; padding: 28px; box-shadow: 0 24px 80px rgba(0,0,0,.55); display: flex; flex-direction: column; gap: 18px; box-sizing: border-box; }
      .rp-panel-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 18px; }
      .rp-close { background: transparent; border: none; color: ${C.textDim}; cursor: pointer; font-size: 22px; }
      .rp-form-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; }
      label, .rp-label { display: flex; flex-direction: column; gap: 7px; color: ${C.textDim}; text-transform: uppercase; letter-spacing: 2px; font-size: 10px; font-weight: 800; }
      input, textarea, select { background: #080808; border: 1px solid ${C.border}; color: ${C.text}; padding: 12px; outline: none; font-family: ${F.body}; letter-spacing: 1px; }
      textarea { min-height: 92px; resize: vertical; line-height: 1.5; }
      label small { color: ${C.textMuted}; letter-spacing: 1px; text-transform: none; font-size: 11px; font-weight: 600; }
      .rp-username-editor, .rp-banner-editor { background: ${C.cardAlt}; border: 1px solid ${C.border}; padding: 14px; display: grid; gap: 12px; }
      .rp-current-username { font-family: ${F.display}; font-size: 22px; letter-spacing: 3px; color: ${C.text}; margin-top: 6px; }
      .rp-username-editor small { color: ${C.textMuted}; letter-spacing: 1px; line-height: 1.4; }
      .rp-username-action { display: grid; grid-template-columns: 1fr auto; gap: 10px; align-items: end; }
      .rp-avatar-editor { background: ${C.cardAlt}; border: 1px solid ${C.border}; padding: 14px; display: grid; grid-template-columns: 120px 1fr; gap: 14px; align-items: start; }
      .rp-avatar-preview { width: 92px; height: 92px; background: #080808; border: 1px solid ${C.borderBright}; display: flex; align-items: center; justify-content: center; margin-top: 8px; overflow: hidden; }
      .rp-avatar-preview img { width: 100%; height: 100%; object-fit: cover; }
      .rp-avatar-preview span { font-family: ${F.display}; font-size: 26px; letter-spacing: 3px; color: ${C.textMuted}; }
      .rp-avatar-controls { display: grid; gap: 10px; }
      .rp-file-upload input { display: none; }
      .rp-file-upload span { border: 1px solid rgba(255,255,255,.15); background: rgba(0,0,0,.28); color: ${C.text}; cursor: pointer; font-family: ${F.display}; letter-spacing: 2px; text-transform: uppercase; padding: 10px 14px; text-align: center; }
      .rp-banner-editor { grid-template-columns: minmax(220px, .7fr) 1fr; align-items: end; }
      .rp-banner-preview { min-height: 74px; border: 1px solid ${C.borderBright}; display: flex; align-items: flex-end; padding: 12px; box-sizing: border-box; overflow: hidden; }
      .rp-banner-preview span { font-family: ${F.display}; letter-spacing: 3px; color: rgba(255,255,255,.78); text-transform: uppercase; }
      .rp-panel-actions { display: flex; justify-content: flex-end; gap: 12px; position: sticky; bottom: -28px; background: ${C.card}; padding-top: 14px; }
      @media (max-width: 1180px) { .rp-grid { grid-template-columns: minmax(260px, .9fr) minmax(360px, 1.1fr); } .rp-stat-strip { grid-template-columns: repeat(3, 1fr); } }
      @media (max-width: 900px) { .rp-grid { grid-template-columns: 1fr; } .rp-main-column { order: -1; } }
      @media (max-width: 760px) { .rp-page { padding: 14px; } .rp-modal { padding: 16px 10px; } .rp-panel { max-height: calc(100vh - 32px); padding: 20px; } .rp-hero { min-height: auto; padding: 78px 18px 20px; } .rp-hero-main { align-items: flex-start; flex-direction: column; padding-top: 0; } .rp-hero-copy h1 { letter-spacing: 5px; } .rp-stat-strip, .rp-grid, .rp-form-grid, .rp-username-action, .rp-avatar-editor, .rp-banner-editor { grid-template-columns: 1fr; } .rp-stat-strip div { border-right: none; border-bottom: 1px solid ${C.border}; } .rp-form-strip { grid-template-columns: repeat(8, minmax(0, 1fr)); } .rp-best-map { align-items: flex-start; flex-direction: column; } .rp-map-rate { width: 100%; border-left: none; border-top: 1px solid ${C.borderBright}; padding: 14px 0 0; text-align: left; } .rp-match { grid-template-columns: 52px 1fr; } .rp-match b, .rp-match em, .rp-match small { text-align: left; } }
      @media (prefers-reduced-motion: reduce) { * { animation-duration: 1ms !important; transition-duration: 1ms !important; } }
    `}</style>
  );
}
